import { Outlet, Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { authenticatedBitswanBackendInstance, getAocHost } from "@/lib/api-client";
import { useAuth } from "@/context/AuthContext";
import { useGlobalAdminStatus } from "@/hooks/useGlobalAdminStatus";
import SupportChatProvider from "@/context/SupportChatProvider";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Key,
  Cloud,
  Users,
  LogOut,
  ChevronUp,
  Shield,
  Receipt,
  Hourglass,
  ShieldAlert,
  BadgeCheck,
  Download,
  RefreshCw,
  Globe,
  KeyRound,
  LayoutDashboard,
  Building2,
  Mail,
  MessageSquare,
  FileText,
  HandCoins,
  Tags,
  Handshake,
  Boxes,
  GitBranch,
  Server,
} from "lucide-react";

const navItems: Array<{ title: string; path: string; icon: typeof Key; support?: boolean; group?: 'enterprise' | 'pricing' | 'distribution' }> = [
  {
    title: "API Keys",
    path: "/api-keys",
    icon: Key,
  },
  {
    title: "Server Configs",
    path: "/server-configs",
    icon: Cloud,
  },
  {
    title: "Billing",
    path: "/billing",
    icon: Receipt,
  },
  {
    title: "Free trials",
    path: "/free-trials",
    icon: Hourglass,
  },
  {
    title: "Customers",
    path: "/customers",
    icon: Building2,
    support: true,
  },
  {
    title: "Servers",
    path: "/servers",
    icon: Server,
    support: true,
  },
  {
    title: "Enterprise clients",
    path: "/enterprise-clients",
    icon: Handshake,
    support: true,
    group: "enterprise",
  },
  {
    title: "Enterprise plans",
    path: "/enterprise-plans",
    icon: Tags,
    support: true,
    group: "enterprise",
  },
  {
    title: "Currency regions",
    path: "/pricing/currency-regions",
    icon: Globe,
    group: "pricing",
  },
  {
    title: "Cloud servers",
    path: "/pricing/cloud-servers",
    icon: Cloud,
    group: "pricing",
  },
  {
    title: "Licence fees",
    path: "/pricing/licence-fees",
    icon: Tags,
    group: "pricing",
  },
  {
    title: "Advisor fees",
    path: "/pricing/advisor-fees",
    icon: Tags,
    group: "pricing",
  },
  {
    title: "On-prem AOC",
    path: "/on-prem-aoc",
    icon: Boxes,
    support: true,
    group: "enterprise",
  },
  {
    title: "Supply chain",
    path: "/supply-chain",
    icon: GitBranch,
    support: true,
    group: "distribution",
  },
  {
    title: "Updates",
    path: "/updates",
    icon: RefreshCw,
    group: "distribution",
  },
  {
    title: "Downloads",
    path: "/downloads",
    icon: Download,
    support: true,
    group: "distribution",
  },
  {
    title: "Registry keys",
    path: "/registry-keys",
    icon: KeyRound,
    group: "distribution",
  },
  {
    title: "Messages",
    path: "/messages",
    icon: MessageSquare,
    support: true,
  },
  {
    title: "Invoices",
    path: "/invoices",
    icon: FileText,
    support: true,
  },
  {
    title: "Payouts",
    path: "/payouts",
    icon: HandCoins,
    support: true,
  },
  {
    title: "Outbox",
    path: "/outbox",
    icon: Mail,
    support: true,
  },
  {
    title: "User Management",
    path: "/users",
    icon: Users,
  },
  {
    title: "Sign-in Providers",
    path: "/sign-in-providers",
    icon: KeyRound,
  },
  {
    title: "Advisors",
    path: "/advisors",
    icon: BadgeCheck,
    support: true,
  },
  {
    title: "Advisor Reports",
    path: "/advisor-reports",
    icon: ShieldAlert,
    support: true,
  },
];

const REPORT_COUNT_POLL_MS = 60000;

export default function GlobalAdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { role } = useGlobalAdminStatus();
  const visible = (item: (typeof navItems)[number]) => role !== 'support' || item.support;
  const supportItems = navItems.filter((item) => item.support && !item.group);
  const adminItems = navItems.filter((item) => !item.support && !item.group);
  const enterpriseItems = navItems.filter((item) => item.group === 'enterprise' && visible(item));
  const pricingItems = navItems.filter((item) => item.group === 'pricing' && visible(item));
  const distributionItems = navItems.filter((item) => item.group === 'distribution' && visible(item));
  const sections = [
    { label: 'Support', items: supportItems },
    { label: 'Enterprise', items: enterpriseItems },
    { label: 'Pricing', items: pricingItems },
    { label: 'Distribution', items: distributionItems },
    ...(role === 'support' ? [] : [{ label: 'Global admin', items: adminItems }]),
  ].filter((section) => section.items.length > 0);

  const reportCounts = useQuery({
    queryKey: ["advisor-report-counts"],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get("/global-admin/advisor-reports/counts/");
      return res.data as { open: number; investigating: number; urgent_open: number };
    },
    refetchInterval: REPORT_COUNT_POLL_MS,
    retry: false,
  });

  const supportCounts = useQuery({
    queryKey: ["support-counts"],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get("/global-admin/support/counts/");
      return res.data as { conversations: number; waiting: number };
    },
    refetchInterval: REPORT_COUNT_POLL_MS,
    retry: false,
  });
  const waitingSupport = supportCounts.data?.waiting ?? 0;

  const openReports = reportCounts.data?.open ?? 0;
  const urgentReports = reportCounts.data?.urgent_open ?? 0;

  const advisorCounts = useQuery({
    queryKey: ["advisor-roster-counts"],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get("/global-admin/advisors/");
      return (res.data as { counts: { awaiting_call: number } }).counts;
    },
    refetchInterval: REPORT_COUNT_POLL_MS,
    retry: false,
  });

  const awaitingCall = advisorCounts.data?.awaiting_call ?? 0;

  const aocDashboardUrl = `${window.location.protocol}//${getAocHost(window.location.hostname)}/dashboard`;

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <SupportChatProvider>
      <Sidebar>
        <SidebarHeader className="border-b border-sidebar-border">
          <div className="flex flex-col items-center px-2 py-3">
            <Link to="/api-keys" className="mb-2">
              <img
                src="/bitswan-logo-dark.svg"
                alt="BitSwan"
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <span className="font-semibold text-xs text-muted-foreground">{role === 'support' ? 'Support' : 'Global Admin'}</span>
            </div>
          </div>
        </SidebarHeader>

        <SidebarContent>
          <ScrollArea className="h-full">
            {sections.map((section) => (
              <SidebarGroup key={section.label}>
                <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {section.items.map((item) => (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          asChild
                          isActive={location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)}
                          tooltip={item.title}
                        >
                          <Link to={item.path}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                            {item.path === "/advisors" && awaitingCall > 0 && (
                              <span
                                className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1.5 text-[10px] font-semibold text-white"
                                title={`${awaitingCall} waiting for a certification call`}
                                data-testid="nav-advisors-badge"
                              >
                                {awaitingCall}
                              </span>
                            )}
                            {item.path === "/messages" && waitingSupport > 0 && (
                              <span
                                className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white"
                                title={`${waitingSupport} waiting for an answer`}
                                data-testid="nav-messages-badge"
                              >
                                {waitingSupport}
                              </span>
                            )}
                            {item.path === "/advisor-reports" && openReports > 0 && (
                              <span
                                className={`ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold text-white ${
                                  urgentReports > 0 ? "bg-red-600" : "bg-red-500"
                                }`}
                                title={
                                  urgentReports > 0
                                    ? `${openReports} open, ${urgentReports} urgent`
                                    : `${openReports} open`
                                }
                                data-testid="nav-advisor-reports-badge"
                              >
                                {openReports}
                              </span>
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}

            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip="Your own organisation: servers, workspaces and people">
                      <a href={aocDashboardUrl} data-testid="nav-back-to-aoc">
                        <LayoutDashboard className="h-4 w-4" />
                        <span>Back to the AOC</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </ScrollArea>
        </SidebarContent>

        <SidebarFooter className="border-t border-sidebar-border">
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  >
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-xs">
                        {user?.name ? getInitials(user.name) : "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{user?.name || "User"}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {user?.email || ""}
                      </span>
                    </div>
                    <ChevronUp className="ml-auto h-4 w-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                  side="top"
                  align="end"
                  sideOffset={4}
                >
                  <div className="flex items-center gap-2 p-2">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-xs">
                        {user?.name ? getInitials(user.name) : "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{user?.name || "User"}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {user?.email || ""}
                      </span>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Global Admin Portal</h1>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </SidebarInset>
    </SupportChatProvider>
  );
}
